import React, { useEffect, useState } from "react";
import { ProCard } from "@ant-design/pro-components";
import axios from "axios";
import getApiUrl from "../../../../../services/getApiUrl";
import {Collapse, Space, Tabs, Tag, Tooltip} from "antd";
import uuid from "react-uuid";
import {getNameFromActionByType, getNameFromTriggerByType, normalizeStepName} from "../Actions/utils/functions";
import _ from "lodash";
import moment from "moment/moment";
import PaginatedJsonView from "./PaginatedJsonViewer";
import "./index.css";
const { Panel } = Collapse;
export const LogViewer = ({ automation, logId = null }) => {
    const [load, setLoad] = useState(false);
    const [automationLogData, setAutomationLogData] = useState(null);
    const loadData = async () => {
        if (logId !== null) {
            try {
                setLoad(true);
                setAutomationLogData(null);
                const { data, status } = await getApiUrl.get(`/automations/logs/${logId}`);
                if (status === 200) {
                    setAutomationLogData(data);
                }
                setLoad(false);
            } catch (ex) {
                setLoad(false);
            }
        }
    };

    useEffect(() => {
        loadData();
    }, [logId]);

    function renderPanelTriggerLog() {
        if(automationLogData){
            const {trigger} = automationLogData;
            return (<Panel header={`Gatilho: ${getNameFromTriggerByType(trigger.type)}`} key={uuid()}>
                <Tabs
                    defaultActiveKey="1"
                    items={[
                        {
                            key: "1",
                            label: "Configurações",
                            children: <PaginatedJsonView key={uuid()} data={_.omit(trigger, ['payload']) ?? {}} />,
                        },
                        {
                            key: "2",
                            label: "Entrada",
                            children: <PaginatedJsonView key={uuid()} data={trigger.payload ?? {}} />,
                        },
                    ]}
                />
            </Panel>)
        }
        return null;
    }

    // Estado do passo: logs novos trazem `status` explícito (inclusive
    // `skipped`, do desvio condicional); logs antigos derivam de data.success.
    const getStepStatus = (step) => {
        if (step.status) return step.status;
        return step.data && step.data.success === false ? 'error' : 'success';
    };

    // Em desvio aninhado a rota é um caminho pontuado (`retirada.ia.protese`) e a
    // lista de rotas ativas ganha um item por nível. Listar tudo no header vira um
    // parágrafo dentro da tag, então mostra as primeiras e joga o resto no tooltip.
    const MAX_VISIBLE_ROUTES = 2;
    const formatRouteList = (routes) => {
        const list = (Array.isArray(routes) ? routes : []).filter(Boolean);
        const full = list.join(', ');
        if (list.length <= MAX_VISIBLE_ROUTES) return { text: full, full, truncated: false };
        return {
            text: `${list.slice(0, MAX_VISIBLE_ROUTES).join(', ')} +${list.length - MAX_VISIBLE_ROUTES}`,
            full,
            truncated: true,
        };
    };

    // `key` fica no elemento externo — em lista, o Tooltip é quem vira o item.
    const withRoutesTooltip = (tag, { full, truncated }, key) => (
        truncated ? <Tooltip key={key} title={full}>{tag}</Tooltip> : tag
    );

    // Nome do passo no header, mesmo critério do canvas: o nome próprio quando
    // houver, senão o nome do tipo. O log guarda o `action` inteiro, e o nome
    // vem como `customName` (é assim que o executor o recebe) — logs anteriores
    // ao recurso simplesmente caem no nome do tipo.
    const getStepName = (action) =>
        normalizeStepName(action.customName ?? action.name) || getNameFromActionByType(action.type);

    // Com nome próprio o tipo some do header, e sem ele não dá para saber que
    // action é aquela ("Consultar Animais Cadastrados" pode ser um script) —
    // então o tipo volta como tag ao lado.
    const renderStepTypeTag = (action) => {
        if (!normalizeStepName(action.customName ?? action.name)) return null;
        return <Tag>{getNameFromActionByType(action.type)}</Tag>;
    };

    const renderStepStatusTag = (step) => {
        const status = getStepStatus(step);
        if (status === 'skipped') {
            const routes = formatRouteList(step.selectedRoutes);
            return withRoutesTooltip(
                <Tag color="default">
                    Pulado{routes.text ? ` — rota ativa: ${routes.text}` : ''}
                </Tag>,
                routes
            );
        }
        if (status === 'error') return <Tag color="red">Erro</Tag>;
        return <Tag color="green">Sucesso</Tag>;
    };

    const renderStepRouteTags = (step) => {
        const tags = [];
        if (step.action && step.action.branch) {
            tags.push(<Tag key="branch" color="geekblue">{step.action.branch}</Tag>);
        }
        // Desvio executado: mostra a rota que venceu (steps.N.value) ou, quando
        // nenhuma casou, o desfecho do fallback — sem isso não dá para saber se
        // a automação encerrou ali ou seguiu no fluxo principal.
        if (step.action && step.action.type === 'switch' && step.data && step.data.result !== undefined && getStepStatus(step) === 'success') {
            const matched = Array.isArray(step.data.result)
                ? step.data.result
                : (step.data.result ? [step.data.result] : []);
            const evaluation = step.data.evaluation || {};
            let label;
            let routes = { full: '', truncated: false };
            if (matched.length > 0) {
                routes = formatRouteList(matched);
                label = `selecionou: ${routes.text}`;
            } else if (evaluation.fallback === 'route') {
                routes = formatRouteList(evaluation.selected);
                label = `nenhum caminho casou — seguiu por: ${routes.text}`;
            } else if (evaluation.fallback === 'continue') {
                label = 'nenhum caminho casou — seguiu no fluxo principal';
            } else {
                label = 'nenhum caminho casou — automação encerrada';
            }
            tags.push(withRoutesTooltip(<Tag key="selected" color="purple">{label}</Tag>, routes, 'selected'));
        }
        // Junção executada: por qual dos caminhos unidos o fluxo chegou até ela
        // — é a única informação que distingue uma execução da outra depois que
        // os caminhos viram um só.
        if (step.action && step.action.type === 'merge' && getStepStatus(step) === 'success') {
            const arrived = Array.isArray(step.data?.evaluation?.arrived)
                ? step.data.evaluation.arrived
                : [];
            if (arrived.length > 0) {
                const routes = formatRouteList(arrived);
                tags.push(withRoutesTooltip(
                    <Tag key="arrived" color="purple">{`chegou por: ${routes.text}`}</Tag>,
                    routes,
                    'arrived'
                ));
            }
        }
        return tags;
    };

    const renderPanelActionLog = () => {
        if (automationLogData) {
            return automationLogData.steps.map((step, index) => {
                const {action, data,endAction,startAction} = step;
                const skipped = getStepStatus(step) === 'skipped';

                return (
                    <Panel key={uuid()} header={
                        <Space wrap>
                            {`Ação ${index + 1}: ${getStepName(action)}`}
                            {renderStepTypeTag(action)}
                            {renderStepStatusTag(step)}
                            {renderStepRouteTags(step)}
                        </Space>
                    }>
                        {automationLogData && !load && !skipped && <div style={{
                            display: 'flex',
                            flexDirection:"column"
                        }}>
                            <p>
                                <label>Data de início: </label>
                                <b>{moment(startAction).format('DD/MM/YYYY HH:mm:ss.SSS')}</b>
                            </p>
                            <p>
                                <label>Data de finalização: </label>
                                <b>{moment(endAction).format('DD/MM/YYYY HH:mm:ss.SSS')}</b>
                            </p>
                            <p>
                                <label>Duração: </label>
                                <b>{moment.utc(moment(endAction).diff(moment(startAction))).format('HH:mm:ss.SSS')}</b>
                            </p>
                        </div>}
                        <Tabs
                            defaultActiveKey="1"
                            items={[
                                {
                                    key: "1",
                                    label: "Configurações",
                                    children: <PaginatedJsonView key={uuid()} data={action.config ?? {}} />,
                                },
                                {
                                    key: "2",
                                    label: "Saída",
                                    children: <PaginatedJsonView key={uuid()} data={(data) ?? {}} />,
                                },
                            ]}
                        />
                    </Panel>
                );
            });
        }
        return [];
    };

    const panels = [
        renderPanelTriggerLog(),
        ...renderPanelActionLog()
    ]

    return (
        <ProCard className="automation-log-viewer" title={"Detalhe"} loading={load}>
            {automationLogData && !load && <div style={{
                display: 'flex',
                flexDirection:"column"
            }}>
                <p>
                    <label>Data de início: </label>
                    <b>{moment(automationLogData.startDate).format('DD/MM/YYYY HH:mm:ss.SSS')}</b>
                </p>
                <p>
                    <label>Data de finalização: </label>
                    <b>{moment(automationLogData.endDate).format('DD/MM/YYYY HH:mm:ss.SSS')}</b>
                </p>
                <p>
                    <label>Duração: </label>
                    <b>{moment.utc(moment(automationLogData.endDate).diff(moment(automationLogData.startDate))).format('HH:mm:ss.SSS')}</b>
                </p>
            </div>}

            <Collapse>
                {panels}
            </Collapse>
        </ProCard>
    );
};
